/* eslint-disable-next-line import/order */
import FullCalendar, {
  DatesSetArg,
  EventContentArg,
  MoreLinkAction,
} from "@fullcalendar/react";

import { EventClickArg, EventSourceInput } from "@fullcalendar/core";
import jaLocale from "@fullcalendar/core/locales/ja";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import ja from "date-fns/locale/ja";
import { useRouter } from "next/router";
import { useState } from "react";

import { NEXT_URL } from "@/utils/const";
import { format } from "@/utils/date";
import { useUserAccount } from "@/states/auth/selectoers";
import ErrorLimitByPlanModal from "@/components/organisms/modals/ErrorLimitByPlanModal";
import { IsDefault, IsMobile } from "@/components/atoms/mediaQuery/MediaQuery";

interface Props {
  events?: EventSourceInput;
  moreLinkClick?: MoreLinkAction;
  handleDateClick?: (arg: DateClickArg) => void;
  eventClick?: (arg: EventClickArg) => void;
  renderEventContent?: (eventInfo: EventContentArg) => void;
  datesSet?: (arg: DatesSetArg) => void;
  isSyncGoogle?: boolean;
}

export function Calendar({
  events,
  moreLinkClick,
  handleDateClick,
  eventClick,
  renderEventContent,
  datesSet,
  isSyncGoogle,
}: Props) {
  const router = useRouter();
  const account = useUserAccount();
  const [isView, setIsView] = useState("dayGridMonth");
  const mediaQueryList = window.matchMedia("(min-width: 768px)");
  const googleBtn = isSyncGoogle ? "activeGoogleBtn" : "defaultGoogleBtn";
  const option = {
    headerToolbar: {
      left: `title prev,today,next`,
      right: `dayGridMonth,timeGridWeek,timeGridDay ${googleBtn}`,
    },
    buttonText: {
      today: "本日",
    },
    dayMaxEventRows: 4,
    contentHeight: 1010,
  };

  /**
   * イベントリスナー
   */
  const listener = (event: MediaQueryList | MediaQueryListEvent) => {
    // リサイズ時に行う処理
    if (event.matches) {
      // 768px以上
      option.headerToolbar = {
        left: `title prev,today,next`,
        right: `dayGridMonth,timeGridWeek,timeGridDay ${googleBtn}`,
      };
      option.dayMaxEventRows = 4;
      option.contentHeight = 1010;
    } else {
      // 768px未満
      option.headerToolbar = {
        left: `title prev,next today`,
        right: `dayGridMonth,timeGridWeek,timeGridDay ${googleBtn}`,
      };
      option.dayMaxEventRows = 0;
      option.contentHeight = 400;
    }
  };

  // リスナー登録
  mediaQueryList.addEventListener("change", listener);

  // 初期化処理
  listener(mediaQueryList);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const handleRedirect = () => {
    if (!account?.user_plan_config?.google_calendar) {
      setIsPopupVisible(true);
      return;
    }
    void router.push(NEXT_URL.ACCOUNTS_SYNC_GOOGLE);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  return (
    <div className={`fc-type-${isView}`}>
      <FullCalendar
        eventClick={eventClick}
        moreLinkClick={moreLinkClick}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        events={events}
        locale={jaLocale}
        initialView="dayGridMonth"
        dayMaxEventRows={option.dayMaxEventRows}
        contentHeight={option.contentHeight}
        eventContent={renderEventContent}
        dateClick={handleDateClick}
        scrollTime="11:00:00"
        eventDisplay="block"
        nowIndicator={true}
        allDaySlot={false}
        headerToolbar={option.headerToolbar}
        buttonText={option.buttonText}
        slotLabelFormat={{
          hour: "numeric",
          minute: "2-digit",
          omitZeroMinute: false,
          meridiem: "short",
        }}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: false,
        }}
        titleRangeSeparator="〜"
        defaultRangeSeparator="〜"
        dayCellContent={function (e) {
          e.dayNumberText = e.dayNumberText.replace("日", "");
        }}
        // Col header week and day
        dayHeaderContent={(e) => {
          const renderDate = () => {
            return e.isToday ? (
              <span
                className="inline-block static w-[26px] h-[26px] bg-primary text-white rounded-full text-center"
                style={{ zIndex: "-1", lineHeight: 1.4 }}
              >
                {format(e.date, "d")}
              </span>
            ) : (
              <span
                className="inline-block static w-[26px] h-[26px] text-center"
                style={{ zIndex: "-1", lineHeight: 1.4 }}
              >
                {format(e.date, "d")}
              </span>
            );
          };
          if (e.view.type === "timeGridWeek") {
            return (
              <>
                <IsDefault>
                  <div className="w-full text-lg font-bold leading-[1.4]">
                    <div className="px-5 py-3 border-b border-[#E5E8EA]">
                      <span className="inline-block static w-[26px] h-[26px] text-center">
                        {format(e.date, "E", { locale: ja })}
                      </span>
                    </div>
                    <div className="px-5 py-3">{renderDate()}</div>
                  </div>
                </IsDefault>
                <IsMobile>
                  <div className="w-full text-lg font-bold bg-white">
                    <div className="">
                      <span className="inline-block static text-center">
                        {format(e.date, "E", { locale: ja })}
                      </span>
                    </div>
                    <div className="">{renderDate()}</div>
                  </div>
                </IsMobile>
              </>
            );
          } else if (e.view.type === "timeGridDay") {
            return (
              <>
                <IsDefault>
                  <div className="w-full text-lg font-bold leading-[1.4]">
                    <div className="text-left px-12 py-3 border-b border-[#E5E8EA]">
                      <span className="inline-block static w-[26px] h-[26px] text-center">
                        {format(e.date, "E", { locale: ja })}
                      </span>
                    </div>
                    <div className="text-left px-12 py-3">{renderDate()}</div>
                  </div>
                </IsDefault>
                <IsMobile>
                  <div className="w-full text-lg font-bold leading-[1.4]">
                    <div className="text-left pl-2">
                      <span className="inline-block static w-[26px] text-center">
                        {format(e.date, "E", { locale: ja })}
                      </span>
                    </div>
                    <div className="text-left pl-2">{renderDate()}</div>
                  </div>
                </IsMobile>
              </>
            );
          }
        }}
        moreLinkContent={(e) => {
          return `他${e.num}件`;
        }}
        customButtons={{
          defaultGoogleBtn: {
            text: "Googleカレンダーと連携",
            click: handleRedirect,
          },
          activeGoogleBtn: {
            text: "Googleカレンダー連携済",
            click: handleRedirect,
          },
        }}
        datesSet={datesSet}
      />
      {isPopupVisible && <ErrorLimitByPlanModal onClose={handleClosePopup} reason="GOOGLE_CALENDAR_HOME" />}
    </div>
  );
}
